"use client";

import Link from "next/link";
import { CalendarDays, Compass, LayoutPanelTop, Radar, Waves } from "lucide-react";

import { SectionHeader } from "@/components/site/primitives/section-header";
import { Button } from "@/components/ui/button";
import { homeSectionAnchors } from "@/lib/content/navigation";
import { captureMarketingEvent } from "@/lib/marketing-analytics-client";

const rolloutSteps = [
  {
    week: "Week 1",
    title: "Choose the problem and set up the team",
    description:
      "Choose one recurring handoff, ownership, or coordination problem. Agree what you will try to change and how you will notice whether it helped, then set up the workspace and onboard the team with clear expectations.",
    icon: LayoutPanelTop,
  },
  {
    week: "Week 2",
    title: "Build the relevant context",
    description:
      "Review the team's Coop Profiles around the chosen problem: what each person needs, where the handoff is losing clarity, and how feedback lands. Choose one practical adjustment to try first.",
    icon: Compass,
  },
  {
    week: "Week 3",
    title: "Turn team signals into a plan",
    description:
      "Run the first Team Fundamentals check across Trust, Conflict, Commitment, Accountability, and Results. Review what it adds to the original problem, then agree the next two or three moves.",
    icon: Radar,
  },
  {
    week: "Week 4",
    title: "Review the evidence and decide what continues",
    description:
      "Review the first Pulse signals against the change you chose in week one. Decide what improved, what still needs attention, and which conversation or weekly rhythm should continue. Then keep going or stop.",
    icon: Waves,
  },
] as const;

export function GuidedRolloutSection() {
  return (
    <section id={homeSectionAnchors.guidedRollout} className="site-section">
      <div className="site-shell site-stack">
        <SectionHeader title="4-week guided rollout" />

        <div className="grid gap-10 lg:grid-cols-[minmax(0,0.78fr)_minmax(0,1.22fr)] lg:gap-12">
          <div className="max-w-xl">
            <div className="lg:sticky lg:top-28">
              <div className="space-y-4 text-base leading-7 text-muted-foreground">
                <p className="site-body">
                  Start with one recurring problem in how the team works: a
                  handoff that keeps returning, an unclear decision owner, or a
                  conversation that keeps slipping.
                </p>
                <p className="site-body">
                  Across four guided weeks, build the relevant context, try one
                  practical change, and review the evidence together.
                </p>
                <p className="site-body">
                  At the end of week 4, decide what should continue or stop.
                </p>
              </div>
              <div className="mt-8">
                <div className="flex flex-col items-start gap-3">
                  <div className="w-fit rounded-[14px] border bg-foreground/10 p-0.5">
                    <Button asChild size="lg" className="rounded-xl px-5 text-base">
                      <Link
                        href="https://docs.google.com/forms/d/e/1FAIpQLSdfoPVan6XMK2p3_iHPwqoQ76VYaKYaoefsocGodsA9-dB_ag/viewform?usp=dialog"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          captureMarketingEvent("guided_rollout_clicked", {
                            app_area: "pricing",
                            cta_id: "guided_rollout_primary",
                            route_name: "home.guided_rollout",
                          });
                        }}
                      >
                        <span className="text-nowrap">Apply for Guided Rollout</span>
                      </Link>
                    </Button>
                  </div>
                  <Button asChild size="lg" variant="ghost" className="h-10.5 rounded-xl px-5 text-base">
                    <Link
                      href="https://cal.eu/cooperly/demo"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2"
                      onClick={() => {
                        captureMarketingEvent("demo_cta_clicked", {
                          app_area: "pricing",
                          cta_id: "guided_rollout_demo",
                          route_name: "home.guided_rollout",
                        });
                      }}
                    >
                      <CalendarDays className="size-4" />
                      <span className="text-nowrap">Book a demo</span>
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="relative grid gap-10 md:gap-12">
            <div
              aria-hidden
              className="absolute bottom-6 left-5 top-6 hidden w-px bg-border/65 lg:block"
            />
            {rolloutSteps.map((step) => (
              <article key={step.week} className="relative pl-0 lg:pl-16">
                <div className="relative">
                  <div className="mb-4 flex items-center gap-3 lg:absolute lg:-left-16 lg:top-0 lg:mb-0 lg:w-16 lg:justify-center">
                    <div className="relative z-10 flex h-10 w-10 shrink-0 rounded-full border border-border/60 bg-background">
                      <step.icon className="m-auto size-4 text-foreground" />
                    </div>
                    <span className="text-sm font-semibold uppercase tracking-[0.12em] text-foreground/80 lg:hidden">
                      {step.week}
                    </span>
                  </div>

                  <div className="pt-0 lg:pt-0">
                    <span className="site-eyebrow hidden font-semibold uppercase tracking-[0.12em] text-foreground/80 lg:inline-block">
                      {step.week}
                    </span>
                    <h3 className="site-block-title mt-2 max-w-xl leading-8">
                      {step.title}
                    </h3>
                    <p className="site-body mt-4 max-w-2xl">
                      {step.description}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
